/**
 * Navigation model — the single source of truth for the rail (CustomMenu),
 * the app-bar section switcher (CustomAppBar) and the browser tab title.
 *
 * The panel is organised by WORK OBJECT, not by backing table: a section is a
 * noun an operator thinks in ("calls", "team", "clients"), and its items are
 * the screens that answer questions about that noun. The rail therefore holds
 * one entry per section (7 for an administrator) and the switch between a
 * section's screens happens in the app bar, one level down.
 *
 * Visibility has exactly one source: `canAccessFeature(item.name)` (role +
 * plan-module gating, mirrored by the conditional `<Resource>` registration in
 * App.tsx). Item names MUST stay in sync with the access-rule keys in
 * `utils/access.ts` and the resource names in `App.tsx`.
 *
 * Collapse rule: when a role can see only ONE item of a section, the rail
 * renders that item directly (its own label and icon) instead of a section
 * that would open onto a single screen. This keeps every role's menu honest —
 * an organization manager never sees a "Clients" entry that holds nothing but
 * its own organizations.
 */

import type { ReactNode } from 'react';
import {
  DashboardRounded as DashboardIcon,
  QrCode2 as QrCodeIcon,
  PhoneInTalkOutlined as CallsIcon,
  History as CallHistoryIcon,
  LanOutlined as ConnectionIcon,
  DynamicFormOutlined as CallerFieldIcon,
  GroupsOutlined as TeamIcon,
  SupportAgent as StaffIcon,
  AccountTree as DepartmentIcon,
  AssignmentInd as StaffScheduleIcon,
  ManageAccounts as OrgManagerIcon,
  HowToRegOutlined as SignupRequestIcon,
  BusinessCenterOutlined as AccountsIcon,
  PersonOutline as ClientIcon,
  Business as OrganizationIcon,
  ReceiptLongOutlined as PaymentIcon,
  WorkspacePremiumOutlined as PlanIcon,
  SettingsRounded as SettingsIcon,
  TuneRounded as ServerSettingsIcon,
  MonitorHeartOutlined as SystemIcon,
  AdminPanelSettingsOutlined as AdminIcon,
} from '@mui/icons-material';
import { canAccessFeature } from '@/utils/access';

/** A single screen: one route, one access rule. */
export interface NavItem {
  /** Access-rule key (`utils/access.ts`) and resource name (`App.tsx`). */
  name: string;
  /** Top-level route this item owns (nested routes match by prefix). */
  to: string;
  /** Translation key for the label. */
  labelKey: string;
  /** Plural form for collection labels (resource names pluralize). */
  smartCount?: number;
  /** Optional role hint surfaced as a tooltip. */
  tooltipKey?: string;
  icon: ReactNode;
}

/** A rail entry: one work object, one or more screens. */
export interface NavSection {
  key: string;
  /**
   * Label used when more than one item is visible. Absent on single-screen
   * sections, which always borrow their item's label.
   */
  labelKey?: string;
  icon: ReactNode;
  items: NavItem[];
}

export const NAV_SECTIONS: NavSection[] = [
  {
    key: 'dashboard',
    icon: <DashboardIcon />,
    items: [{ name: 'dashboard', to: '/', labelKey: 'ra.page.dashboard', icon: <DashboardIcon /> }],
  },
  {
    // The product's central object gets its own entry: it is the most visited
    // screen of the panel and must never cost more than one click.
    key: 'qrcodes',
    icon: <QrCodeIcon />,
    items: [
      {
        name: 'qrcodes',
        to: '/qrcodes',
        labelKey: 'resources.qrcodes.name',
        smartCount: 2,
        icon: <QrCodeIcon />,
      },
    ],
  },
  {
    key: 'calls',
    labelKey: 'common.nav.sections.calls',
    icon: <CallsIcon />,
    items: [
      {
        name: 'callDetails',
        to: '/callDetails',
        labelKey: 'resources.callDetails.name',
        smartCount: 2,
        icon: <CallHistoryIcon />,
      },
      {
        name: 'connections',
        to: '/connections',
        labelKey: 'resources.connections.name',
        smartCount: 2,
        icon: <ConnectionIcon />,
      },
      {
        name: 'qrUserDetails',
        to: '/qrUserDetails',
        labelKey: 'resources.qrUserDetails.name',
        smartCount: 2,
        icon: <CallerFieldIcon />,
      },
    ],
  },
  {
    key: 'team',
    labelKey: 'common.nav.sections.team',
    icon: <TeamIcon />,
    items: [
      {
        name: 'staff',
        to: '/staff',
        labelKey: 'resources.staff.name',
        smartCount: 2,
        tooltipKey: 'common.menu.tooltips.staff',
        icon: <StaffIcon />,
      },
      {
        name: 'departments',
        to: '/departments',
        labelKey: 'resources.departments.name',
        smartCount: 2,
        icon: <DepartmentIcon />,
      },
      {
        name: 'userGroupOrganization',
        to: '/userGroupOrganization',
        labelKey: 'resources.userGroupOrganization.name',
        smartCount: 2,
        icon: <StaffScheduleIcon />,
      },
      {
        name: 'orgManagers',
        to: '/orgManagers',
        labelKey: 'resources.orgManagers.name',
        smartCount: 2,
        tooltipKey: 'common.menu.tooltips.orgManagers',
        icon: <OrgManagerIcon />,
      },
    ],
  },
  {
    // Kept out of the accounts section on purpose: while the signup funnel is
    // open this tray is worked daily, and burying it behind another entry
    // would be the one place a section costs a real click.
    key: 'signupRequests',
    icon: <SignupRequestIcon />,
    items: [
      {
        name: 'signupRequests',
        to: '/signupRequests',
        labelKey: 'resources.signupRequests.name',
        smartCount: 2,
        icon: <SignupRequestIcon />,
      },
    ],
  },
  {
    key: 'clients',
    labelKey: 'common.nav.sections.clients',
    icon: <AccountsIcon />,
    items: [
      {
        name: 'clients',
        to: '/clients',
        labelKey: 'resources.clients.name',
        smartCount: 2,
        tooltipKey: 'common.menu.tooltips.clients',
        icon: <ClientIcon />,
      },
      {
        name: 'organizations',
        to: '/organizations',
        labelKey: 'resources.organizations.name',
        smartCount: 2,
        icon: <OrganizationIcon />,
      },
      {
        name: 'transactions',
        to: '/transactions',
        labelKey: 'resources.transactions.name',
        smartCount: 2,
        icon: <PaymentIcon />,
      },
      {
        name: 'plans',
        to: '/plans',
        labelKey: 'resources.plans.name',
        smartCount: 2,
        icon: <PlanIcon />,
      },
    ],
  },
  {
    key: 'settings',
    labelKey: 'common.nav.sections.settings',
    icon: <SettingsIcon />,
    items: [
      {
        name: 'server-settings',
        to: '/server-settings',
        labelKey: 'pages.serverSettings.title',
        icon: <ServerSettingsIcon />,
      },
      { name: 'system', to: '/system', labelKey: 'pages.system.title', icon: <SystemIcon /> },
      {
        name: 'admins',
        to: '/admins',
        labelKey: 'resources.admins.name',
        smartCount: 2,
        icon: <AdminIcon />,
      },
    ],
  },
];

/** Sections the current user may see, with their items already filtered. */
export const getVisibleSections = (): NavSection[] =>
  NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => canAccessFeature(item.name)),
  })).filter((section) => section.items.length > 0);

/**
 * The label a section shows in the rail: its own when it opens onto several
 * screens, its only item's when it collapses (see the collapse rule above).
 */
export const railLabelOf = (section: NavSection): { labelKey: string; smartCount: number } => {
  if (section.labelKey && section.items.length > 1) {
    return { labelKey: section.labelKey, smartCount: 1 };
  }
  const [item] = section.items;
  return { labelKey: item.labelKey, smartCount: item.smartCount ?? 1 };
};

/** The icon a section shows in the rail, following the same collapse rule. */
export const railIconOf = (section: NavSection): ReactNode =>
  section.items.length > 1 ? section.icon : section.items[0].icon;

/** Whether a route belongs to an item (exact match, or a nested record/form). */
const matchesItem = (pathname: string, to: string): boolean =>
  to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`);

/** Resolve a route to its section and item within the given sections. */
export const resolveRoute = (
  pathname: string,
  sections: NavSection[]
): { section: NavSection; item: NavItem } | null => {
  for (const section of sections) {
    for (const item of section.items) {
      if (matchesItem(pathname, item.to)) return { section, item };
    }
  }
  return null;
};
